import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, RefreshCw, Mail, CheckCircle2, XCircle, Save, Copy, Star, Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudLightning, Snowflake, Droplets, Wind, Send, CheckCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import AvailabilityAdmin from "@/components/AvailabilityAdmin";

const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const INQUIRY_STATUSES = ["new", "read", "resolved"];

export default function AdminDashboardPage() {
  const { user, initialized, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("schedule");
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendState, setResendState] = useState({}); // {orderId: 'sending'|'ok'|'err'}
  const [reviewState, setReviewState] = useState({}); // {bookingId: 'sending'|'ok'|'err'}
  const [bulkState, setBulkState] = useState({ completePast: null, markAllRead: null });
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, i, o, s, a] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/inquiries"),
        api.get("/admin/orders"),
        api.get("/admin/stats"),
        api.get("/admin/analytics").catch(() => ({ data: null })),
      ]);
      setBookings(b.data);
      setInquiries(i.data);
      setOrders(o.data);
      setStats(s.data);
      setAnalytics(a.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line
  }, [user]);

  if (!initialized) return <div className="min-h-screen bg-[#050505]" />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const updateBooking = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    load();
  };

  const updateInquiry = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    setInquiries((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    load();
  };

  const resendOrderEmails = async (id) => {
    setResendState((s) => ({ ...s, [id]: "sending" }));
    try {
      const { data } = await api.post(`/admin/orders/${id}/resend-emails`);
      const ok = data?.owner?.ok || data?.customer?.ok;
      setResendState((s) => ({ ...s, [id]: ok ? "ok" : "err" }));
    } catch (e) {
      setResendState((s) => ({ ...s, [id]: "err" }));
    } finally {
      setTimeout(() => setResendState((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      }), 3200);
    }
  };

  const resendBookingEmails = async (id) => {
    setResendState((s) => ({ ...s, [id]: "sending" }));
    try {
      const { data } = await api.post(`/admin/bookings/${id}/resend-emails`);
      const ok = data?.owner?.ok || data?.customer?.ok;
      setResendState((s) => ({ ...s, [id]: ok ? "ok" : "err" }));
    } catch (e) {
      setResendState((s) => ({ ...s, [id]: "err" }));
    } finally {
      setTimeout(() => setResendState((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      }), 3200);
    }
  };

  const sendReviewRequest = async (id) => {
    setReviewState((s) => ({ ...s, [id]: "sending" }));
    try {
      const { data } = await api.post(`/admin/bookings/${id}/send-review-request`);
      setReviewState((s) => ({ ...s, [id]: data?.email?.ok ? "ok" : "err" }));
      // refresh so the button reflects review_request_sent = true
      load();
    } catch (e) {
      setReviewState((s) => ({ ...s, [id]: "err" }));
    } finally {
      setTimeout(() => setReviewState((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      }), 3200);
    }
  };

  const completePastBookings = async () => {
    setBulkState((s) => ({ ...s, completePast: "sending" }));
    try {
      const { data } = await api.post("/admin/bookings/complete-past");
      setBulkState((s) => ({ ...s, completePast: "ok" }));
      // eslint-disable-next-line no-alert
      alert(`Marked ${data.updated} past booking${data.updated === 1 ? "" : "s"} as completed.`);
      load();
    } catch (e) {
      setBulkState((s) => ({ ...s, completePast: "err" }));
    } finally {
      setTimeout(() => setBulkState((s) => ({ ...s, completePast: null })), 3200);
    }
  };

  const markAllInquiriesRead = async () => {
    setBulkState((s) => ({ ...s, markAllRead: "sending" }));
    try {
      const { data } = await api.post("/admin/inquiries/mark-all-read");
      setBulkState((s) => ({ ...s, markAllRead: "ok" }));
      // eslint-disable-next-line no-alert
      alert(`Marked ${data.updated} inquir${data.updated === 1 ? "y" : "ies"} as read.`);
      load();
    } catch (e) {
      setBulkState((s) => ({ ...s, markAllRead: "err" }));
    } finally {
      setTimeout(() => setBulkState((s) => ({ ...s, markAllRead: null })), 3200);
    }
  };

  const updateFulfillment = async (id, patch) => {
    // Optimistic UI
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, ...patch } : o))
    );
    try {
      await api.patch(`/admin/orders/${id}/fulfillment`, patch);
    } catch (e) {
      // Revert by reloading on failure
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <p className="eyebrow mb-1">SEVEN7H · Admin</p>
            <h1 className="font-serif text-2xl md:text-3xl">
              Welcome, <span className="italic text-neutral-400">{user.name}.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="btn-ghost !py-2.5 !px-4 !text-[11px]"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              data-testid={ADMIN.logout}
              onClick={onLogout}
              className="btn-ghost !py-2.5 !px-4 !text-[11px]"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
          {[
            ["Total Bookings", stats?.total_bookings ?? "—"],
            ["Pending", stats?.pending_bookings ?? "—"],
            ["Orders Paid", stats?.paid_orders ?? "—"],
            ["Total Inquiries", stats?.total_inquiries ?? "—"],
            ["New Messages", stats?.new_inquiries ?? "—"],
          ].map(([label, val], i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="border border-white/10 p-6"
            >
              <p className="eyebrow mb-4">{label}</p>
              <p className="font-serif text-4xl md:text-5xl">{val}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-8 border-b border-white/10 mb-8 overflow-x-auto">
          <button
            data-testid="admin-tab-schedule"
            onClick={() => setTab("schedule")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors whitespace-nowrap ${
              tab === "schedule" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Schedule
          </button>
          <button
            data-testid="admin-tab-analytics"
            onClick={() => setTab("analytics")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors whitespace-nowrap ${
              tab === "analytics" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Analytics {analytics?.live > 0 && <span className="ml-1 text-brand-red">· {analytics.live} live</span>}
          </button>
          <button
            data-testid={ADMIN.tabBookings}
            onClick={() => setTab("bookings")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors whitespace-nowrap ${
              tab === "bookings" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Bookings ({bookings.length})
          </button>
          <button
            data-testid={ADMIN.tabInquiries}
            onClick={() => setTab("inquiries")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors ${
              tab === "inquiries" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Inquiries ({inquiries.length})
          </button>
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors ${
              tab === "orders" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Shop Orders ({orders.length})
          </button>
          <button
            data-testid="admin-tab-availability"
            onClick={() => setTab("availability")}
            className={`pb-4 font-mono text-[11px] tracking-[0.24em] uppercase transition-colors ${
              tab === "availability" ? "text-white border-b border-white -mb-px" : "text-neutral-500"
            }`}
          >
            Availability
          </button>
        </div>

        <AnimatePresence mode="wait">
          {tab === "schedule" ? (
            <motion.div
              key="sched"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <DailySchedule bookings={bookings} />
            </motion.div>
          ) : tab === "analytics" ? (
            <motion.div
              key="an"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <AnalyticsPanel analytics={analytics} onRefresh={load} />
            </motion.div>
          ) : tab === "availability" ? (
            <motion.div
              key="av"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <AvailabilityAdmin />
            </motion.div>
          ) : tab === "orders" ? (
            <motion.div
              key="o"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {orders.length === 0 ? (
                <Empty label="No shop orders yet." />
              ) : (
                <ul className="space-y-4">
                  {orders.map((o) => (
                    <OrderCard
                      key={o.id}
                      order={o}
                      resendState={resendState[o.id]}
                      onResend={() => resendOrderEmails(o.id)}
                      onFulfillmentChange={(patch) =>
                        updateFulfillment(o.id, patch)
                      }
                    />
                  ))}
                </ul>
              )}
            </motion.div>
          ) : tab === "bookings" ? (
            <motion.div
              key="b"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {bookings.length === 0 ? (
                <Empty label="No bookings yet." />
              ) : (
                <div className="overflow-x-auto">
                  <div className="flex items-center justify-end mb-4">
                    <BulkActionButton
                      testId="admin-complete-past-bookings"
                      state={bulkState.completePast}
                      onClick={completePastBookings}
                      label="Complete Past"
                      confirmMessage="Mark every past-date booking as 'completed'? This won't touch cancelled or already-completed ones."
                    />
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-500 font-mono text-[10px] tracking-[0.24em] uppercase border-b border-white/10">
                        <th className="py-4 pr-4">Date</th>
                        <th className="py-4 pr-4">Client</th>
                        <th className="py-4 pr-4">Contact</th>
                        <th className="py-4 pr-4">Service</th>
                        <th className="py-4 pr-4">When</th>
                        <th className="py-4 pr-4">Vehicle</th>
                        <th className="py-4 pr-4">Status</th>
                        <th className="py-4 pr-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr
                          key={b.id}
                          data-testid={ADMIN.bookingRow(b.id)}
                          className="border-b border-white/5 hover:bg-white/[0.02]"
                        >
                          <td className="py-4 pr-4 text-neutral-400">
                            {new Date(b.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 pr-4 text-white">{b.name}</td>
                          <td className="py-4 pr-4 text-neutral-400">
                            <div>{b.email}</div>
                            <div className="text-xs">{b.phone}</div>
                          </td>
                          <td className="py-4 pr-4 text-neutral-400">
                            {b.service}
                            {b.add_engine_bay && (
                              <span className="block text-xs text-neutral-600">
                                + engine bay
                              </span>
                            )}
                          </td>
                          <td className="py-4 pr-4 text-neutral-400">
                            {b.preferred_date}
                            <div className="text-xs">{b.preferred_time}</div>
                          </td>
                          <td className="py-4 pr-4 text-neutral-400">
                            {b.vehicle_type}
                            {b.vehicle_make_model && (
                              <div className="text-xs">{b.vehicle_make_model}</div>
                            )}
                          </td>
                          <td className="py-4 pr-4">
                            <select
                              value={b.status}
                              data-testid={ADMIN.bookingStatusSelect(b.id)}
                              onChange={(e) => updateBooking(b.id, e.target.value)}
                              className="bg-[#0a0a0a] border border-white/10 text-white text-xs px-3 py-2 font-mono uppercase tracking-widest focus:outline-none focus:border-white/40"
                            >
                              {BOOKING_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-4 pr-4 text-right">
                            <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                              <CopyBookingButton booking={b} />
                              <EmailCustomerButton booking={b} />
                              <ReviewRequestButton
                                booking={b}
                                state={reviewState[b.id]}
                                onClick={() => sendReviewRequest(b.id)}
                              />
                              <ResendButton
                                state={resendState[b.id]}
                                onClick={() => resendBookingEmails(b.id)}
                                testId={`admin-booking-resend-${b.id}`}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="i"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {inquiries.length === 0 ? (
                <Empty label="No inquiries yet." />
              ) : (
                <>
                  <div className="flex items-center justify-end mb-4">
                    <BulkActionButton
                      testId="admin-mark-all-inquiries-read"
                      state={bulkState.markAllRead}
                      onClick={markAllInquiriesRead}
                      label="Mark All Read"
                      confirmMessage="Mark every new inquiry as 'read'?"
                    />
                  </div>
                  <ul className="divide-y divide-white/10">
                  {inquiries.map((iq) => (
                    <li
                      key={iq.id}
                      data-testid={ADMIN.inquiryRow(iq.id)}
                      className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4"
                    >
                      <div className="md:col-span-3">
                        <p className="eyebrow mb-1">
                          {new Date(iq.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-white">{iq.name}</p>
                        <p className="text-neutral-500 text-xs">{iq.email}</p>
                        {iq.phone && (
                          <p className="text-neutral-500 text-xs">{iq.phone}</p>
                        )}
                      </div>
                      <div className="md:col-span-6">
                        <p className="font-serif text-lg text-white mb-1">
                          {iq.subject}
                        </p>
                        <p className="text-neutral-400 text-sm whitespace-pre-wrap">
                          {iq.message}
                        </p>
                      </div>
                      <div className="md:col-span-3 flex md:justify-end items-start">
                        <select
                          value={iq.status}
                          data-testid={ADMIN.inquiryStatusSelect(iq.id)}
                          onChange={(e) => updateInquiry(iq.id, e.target.value)}
                          className="bg-[#0a0a0a] border border-white/10 text-white text-xs px-3 py-2 font-mono uppercase tracking-widest focus:outline-none focus:border-white/40"
                        >
                          {INQUIRY_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </li>
                  ))}
                </ul>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}



const WEATHER_ICON = {
  sun: Sun,
  cloud: Cloud,
  "cloud-sun": CloudSun,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-lightning": CloudLightning,
  snowflake: Snowflake,
};

const WEATHER_VERDICT = {
  great: {
    label: "Perfect for wax + paint correction",
    color: "text-brand-red",
    dot: "bg-brand-red",
  },
  good: {
    label: "Fine for wash + interior detail",
    color: "text-neutral-300",
    dot: "bg-white/60",
  },
  poor: {
    label: "Interior-only day — wax won't cure",
    color: "text-amber-400",
    dot: "bg-amber-400",
  },
  bad: {
    label: "Reschedule outdoor work if possible",
    color: "text-red-500",
    dot: "bg-red-500",
  },
};

function WeatherChip({ weather }) {
  const cond = weather.condition || {};
  const Icon = WEATHER_ICON[cond.icon] || Cloud;
  const verdict = WEATHER_VERDICT[cond.score] || WEATHER_VERDICT.good;
  const rain = weather.precipitation_probability ?? 0;
  return (
    <div
      className="border-b border-white/10 px-6 py-4 flex items-center gap-4"
      data-testid="schedule-weather-chip"
    >
      <div className="shrink-0 w-10 h-10 flex items-center justify-center border border-white/10 bg-white/5">
        <Icon size={20} className="text-white/80" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-serif text-2xl text-white leading-none">
            {Math.round(weather.temp_max)}°
          </span>
          <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            / {Math.round(weather.temp_min)}°
          </span>
          <span className="text-neutral-300 text-sm">{cond.label}</span>
        </div>
        <div className={`mt-1 flex items-center gap-2 text-[11px] ${verdict.color}`}>
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${verdict.dot}`}
          />
          <span className="uppercase tracking-widest font-mono text-[9px]">
            {verdict.label}
          </span>
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-neutral-500 text-[10px] font-mono tracking-widest uppercase shrink-0">
        <span className="inline-flex items-center gap-1">
          <Droplets size={10} /> {rain}%
        </span>
        <span className="inline-flex items-center gap-1">
          <Wind size={10} /> {Math.round(weather.wind_kmh)}k
        </span>
      </div>
    </div>
  );
}


function AnalyticsPanel({ analytics, onRefresh }) {
  const a = analytics;
  if (!a) {
    return (
      <div className="py-20 text-center border border-dashed border-white/10">
        <p className="text-neutral-500">Loading analytics…</p>
      </div>
    );
  }
  const maxDaily = Math.max(1, ...a.daily.map((d) => d.count));
  return (
    <div className="space-y-10" data-testid="admin-analytics">
      {/* Live + big stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BigStat
          label={
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
              Live · Last 5 min
            </span>
          }
          value={a.live}
          testId="analytics-live"
        />
        <BigStat
          label="Today"
          value={a.today_unique}
          sub={`${a.today_views} view${a.today_views === 1 ? "" : "s"}`}
          testId="analytics-today"
        />
        <BigStat
          label="This Week"
          value={a.week_unique}
          sub={`${a.week_views} views`}
          testId="analytics-week"
        />
        <BigStat
          label="This Month"
          value={a.month_unique}
          sub={`${a.month_views} views`}
          testId="analytics-month"
        />
      </div>

      {/* 7-day bar chart */}
      <section className="border border-white/10 p-6 md:p-8">
        <div className="flex items-baseline justify-between mb-6">
          <p className="eyebrow">Traffic — last 7 days</p>
          <button
            onClick={onRefresh}
            className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        <div className="grid grid-cols-7 gap-2 md:gap-3 items-end h-40">
          {a.daily.map((d) => {
            const h = Math.round((d.count / maxDaily) * 100);
            const dayLabel = new Date(d.date + "T00:00:00").toLocaleDateString(
              undefined,
              { weekday: "short" }
            );
            return (
              <div key={d.date} className="flex flex-col items-center gap-2 h-full justify-end">
                <span className="font-mono text-[10px] text-neutral-400">{d.count}</span>
                <div
                  className="w-full bg-brand-red/70 hover:bg-brand-red transition-colors"
                  style={{ height: `${h}%`, minHeight: d.count > 0 ? "3px" : "1px" }}
                  title={`${d.date}: ${d.count} views`}
                />
                <span className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
                  {dayLabel}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Top pages / sources / devices */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <RankList
          eyebrow="Top pages"
          items={a.top_pages}
          formatter={(v) => v || "/"}
        />
        <RankList
          eyebrow="Top sources"
          items={a.top_sources}
          formatter={(v) => v}
        />
        <RankList eyebrow="Devices" items={a.top_devices} formatter={(v) => v} />
      </div>

      <p className="pt-8 border-t border-white/10 text-neutral-500 text-xs leading-relaxed max-w-xl">
        Privacy-first — no cookies stored on visitors. Uniques are counted by a
        daily-rotating IP hash so nothing personally identifying is kept.
      </p>
    </div>
  );
}

function BigStat({ label, value, sub, testId }) {
  return (
    <div className="border border-white/10 p-5 md:p-6" data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-3">
        {label}
      </p>
      <p className="font-serif text-4xl md:text-5xl text-white leading-none">
        {value}
      </p>
      {sub && (
        <p className="mt-2 font-mono text-[10px] tracking-widest uppercase text-neutral-600">
          {sub}
        </p>
      )}
    </div>
  );
}

function RankList({ eyebrow, items, formatter }) {
  return (
    <section className="border border-white/10 p-6">
      <p className="eyebrow mb-4">{eyebrow}</p>
      {items.length === 0 ? (
        <p className="text-neutral-500 text-sm italic">Nothing yet.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li
              key={it.label}
              className="flex items-baseline justify-between gap-3 text-sm border-b border-white/5 pb-2 last:border-0"
            >
              <span className="text-white truncate">{formatter(it.label)}</span>
              <span className="font-mono text-neutral-400 shrink-0">{it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DailySchedule({ bookings }) {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    api
      .get("/weather")
      .then((r) => setWeather(r.data))
      .catch(() => setWeather(null));
  }, []);

  const iso = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const activeBookings = (bookings || []).filter(
    (b) => b.status !== "cancelled"
  );
  const forDate = (dateKey) =>
    activeBookings
      .filter((b) => b.preferred_date === dateKey)
      .sort((a, b) => (a.preferred_time || "").localeCompare(b.preferred_time || ""));

  const todayList = forDate(iso(today));
  const tomorrowList = forDate(iso(tomorrow));
  const upcomingCount = activeBookings.filter(
    (b) => b.preferred_date && b.preferred_date > iso(tomorrow)
  ).length;

  const weatherForDate = (key) =>
    weather?.days?.find((d) => d.date === key);

  return (
    <div data-testid="admin-schedule">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
        <ScheduleColumn
          eyebrow="Today"
          date={today}
          bookings={todayList}
          highlightNow
          weather={weatherForDate(iso(today))}
        />
        <ScheduleColumn
          eyebrow="Tomorrow"
          date={tomorrow}
          bookings={tomorrowList}
          weather={weatherForDate(iso(tomorrow))}
        />
      </div>

      {upcomingCount > 0 && (
        <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between text-sm text-neutral-500">
          <p>
            <span className="text-white font-mono">{upcomingCount}</span> more
            active booking{upcomingCount === 1 ? "" : "s"} on future dates —
            switch to the <span className="text-white">Bookings</span> tab to
            see everything.
          </p>
        </div>
      )}
    </div>
  );
}

function ScheduleColumn({ eyebrow, date, bookings, highlightNow, weather }) {
  const dayLabel = date.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const dateLabel = date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
  });
  const nowHHMM = highlightNow
    ? `${String(date.getHours()).padStart(2, "0")}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`
    : null;

  return (
    <section className="border border-white/10 bg-white/[0.015]">
      <header className="px-6 py-5 border-b border-white/10 flex items-end justify-between">
        <div>
          <p className="eyebrow mb-2">{eyebrow}</p>
          <h3 className="font-serif text-3xl md:text-4xl text-white leading-none">
            {dayLabel}
          </h3>
          <p className="mt-1 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            {dateLabel}
          </p>
        </div>
        <div className="text-right">
          <p className="font-serif text-4xl text-white leading-none">
            {bookings.length}
          </p>
          <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500 mt-1">
            Booking{bookings.length === 1 ? "" : "s"}
          </p>
        </div>
      </header>

      {weather && <WeatherChip weather={weather} />}

      {bookings.length === 0 ? (
        <div className="p-12 text-center">
          <p className="font-serif text-lg text-neutral-400 italic">
            No bookings.
          </p>
          <p className="mt-1 font-mono text-[10px] tracking-widest uppercase text-neutral-600">
            Enjoy the day off, Kosta.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-white/10">
          {bookings.map((b) => {
            const isPast = nowHHMM && b.preferred_time && b.preferred_time < nowHHMM;
            return (
              <li
                key={b.id}
                data-testid={`schedule-row-${b.id}`}
                className={`grid grid-cols-12 gap-3 items-center px-6 py-5 hover:bg-white/[0.02] transition-colors ${
                  isPast ? "opacity-40" : ""
                }`}
              >
                <div className="col-span-3 md:col-span-2">
                  <p className="font-serif text-2xl md:text-3xl text-white leading-none">
                    {b.preferred_time}
                  </p>
                  {isPast && (
                    <p className="mt-1 font-mono text-[9px] tracking-widest uppercase text-brand-red">
                      Done
                    </p>
                  )}
                </div>
                <div className="col-span-9 md:col-span-7 min-w-0">
                  <p className="text-white truncate">{b.name}</p>
                  <p className="text-neutral-400 text-xs truncate">
                    {b.service?.replace(/-/g, " ")}
                    {b.add_engine_bay && (
                      <span className="text-neutral-500"> · + engine bay</span>
                    )}
                  </p>
                  <p className="text-neutral-500 text-xs truncate">
                    {b.vehicle_type}
                    {b.vehicle_make_model && ` · ${b.vehicle_make_model}`}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-3 md:text-right">
                  {b.phone && (
                    <a
                      href={`tel:${b.phone}`}
                      className="font-mono text-[11px] tracking-widest uppercase text-brand-red hover:text-white transition-colors"
                      data-testid={`schedule-call-${b.id}`}
                    >
                      {b.phone}
                    </a>
                  )}
                  <div>
                    <span
                      className={`mt-1 inline-block px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase ${
                        b.status === "confirmed"
                          ? "bg-brand-red/15 text-brand-red border border-brand-red/40"
                          : "bg-white/5 text-neutral-500 border border-white/10"
                      }`}
                    >
                      {b.status}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Empty({ label }) {
  return (
    <div className="py-20 text-center border border-dashed border-white/10">
      <p className="font-serif text-2xl text-neutral-500">{label}</p>
    </div>
  );
}

function ResendButton({ state, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={state === "sending"}
      data-testid={testId}
      className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      {state === "sending" ? (
        <>
          <RefreshCw size={12} className="animate-spin" /> Sending
        </>
      ) : state === "ok" ? (
        <>
          <CheckCircle2 size={12} className="text-brand-red" /> Sent
        </>
      ) : state === "err" ? (
        <>
          <XCircle size={12} className="text-red-500" /> Failed
        </>
      ) : (
        <>
          <Mail size={12} /> Resend
        </>
      )}
    </button>
  );
}


function CopyBookingButton({ booking }) {
  const [copied, setCopied] = useState(false);

  const buildMessage = () => {
    const first = (booking.name || "").split(" ")[0] || "there";
    const dateObj = booking.preferred_date
      ? new Date(booking.preferred_date + "T00:00:00")
      : null;
    const dateStr = dateObj
      ? dateObj.toLocaleDateString(undefined, {
          weekday: "short",
          day: "numeric",
          month: "long",
        })
      : booking.preferred_date;
    const svc = (booking.service || "detail")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return `Hi ${first} — Kosta here from SEVEN7H. Just confirming your ${svc} for ${dateStr} at ${booking.preferred_time}. Address: 39 Reynolds Drive, Paralowie. Any changes call 0429 052 189. Cheers!`;
  };

  const onCopy = async () => {
    const msg = buildMessage();
    try {
      await navigator.clipboard.writeText(msg);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = msg;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <button
      onClick={onCopy}
      data-testid={`admin-booking-copy-${booking.id}`}
      title="Copy a personalised confirmation message to your clipboard, then paste into SMS / iMessage / WhatsApp"
      className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase transition-colors"
    >
      {copied ? (
        <>
          <CheckCircle2 size={12} className="text-brand-red" /> Copied
        </>
      ) : (
        <>
          <Copy size={12} /> Copy Text
        </>
      )}
    </button>
  );
}

function ReviewRequestButton({ booking, state, onClick }) {
  const alreadySent = booking.review_request_sent;
  const disabled = state === "sending" || alreadySent;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`admin-booking-review-${booking.id}`}
      title={
        alreadySent
          ? "Review request already sent for this booking"
          : "Send a Google-review request email to this customer"
      }
      className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      {state === "sending" ? (
        <>
          <RefreshCw size={12} className="animate-spin" /> Sending
        </>
      ) : state === "ok" || alreadySent ? (
        <>
          <CheckCircle2 size={12} className="text-brand-red" /> Review Sent
        </>
      ) : state === "err" ? (
        <>
          <XCircle size={12} className="text-red-500" /> Failed
        </>
      ) : (
        <>
          <Star size={12} /> Request Review
        </>
      )}
    </button>
  );
}



const FULFILLMENT_STEPS = [
  { key: "received", label: "Received" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

function OrderCard({ order, resendState, onResend, onFulfillmentChange }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [expanded, setExpanded] = useState(false);
  const activeIdx = Math.max(
    0,
    FULFILLMENT_STEPS.findIndex((s) => s.key === (order.fulfillment_status || "received"))
  );
  const short = order.id.slice(0, 8).toUpperCase();
  const rs = resendState;

  const setStep = (key) => {
    // If moving to 'shipped', include the current tracking fields so the
    // customer email includes them. Otherwise, just update the state.
    const patch = { fulfillment_status: key };
    if (key === "shipped") {
      patch.tracking_number = tracking || null;
      patch.tracking_url = trackingUrl || null;
      patch.carrier = carrier || null;
    }
    onFulfillmentChange(patch);
  };

  const saveTracking = () => {
    onFulfillmentChange({
      fulfillment_status: order.fulfillment_status || "received",
      tracking_number: tracking || null,
      tracking_url: trackingUrl || null,
      carrier: carrier || null,
    });
  };

  return (
    <li
      data-testid={`admin-order-row-${order.id}`}
      className="border border-white/10 hover:border-white/20 transition-colors"
    >
      <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        {/* Ref + date */}
        <div className="md:col-span-2">
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-1">
            #{short}
          </p>
          <p className="text-neutral-400 text-xs">
            {new Date(order.created_at).toLocaleDateString()}
          </p>
          <span
            className={`mt-3 inline-block px-2 py-1 font-mono text-[9px] tracking-widest uppercase ${
              order.paid
                ? "bg-brand-red/15 text-brand-red border border-brand-red/40"
                : "bg-white/5 text-neutral-500 border border-white/10"
            }`}
          >
            {order.paid ? "paid" : order.status || "pending"}
          </span>
        </div>

        {/* Customer */}
        <div className="md:col-span-3">
          <p className="text-white leading-tight">{order.name}</p>
          <p className="text-neutral-500 text-xs mt-0.5">{order.email}</p>
          {order.phone && (
            <p className="text-neutral-500 text-xs">{order.phone}</p>
          )}
        </div>

        {/* Items summary */}
        <div className="md:col-span-4">
          <ul className="text-neutral-400 text-xs space-y-1">
            {(order.items || []).map((it) => (
              <li key={it.slug} className="truncate">
                <span className="text-white">{it.quantity}×</span> {it.name}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setExpanded((x) => !x)}
            className="mt-2 font-mono text-[9px] tracking-widest uppercase text-neutral-500 hover:text-white"
            data-testid={`admin-order-toggle-${order.id}`}
          >
            {expanded ? "Hide details" : "Ship-to details"}
          </button>
        </div>

        {/* Total + resend */}
        <div className="md:col-span-3 md:text-right">
          <p className="font-serif text-3xl text-white">
            ${Number(order.total || 0).toFixed(2)}
          </p>
          <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
            {order.payment_method?.toUpperCase() || "—"}
          </p>
          <button
            onClick={onResend}
            disabled={rs === "sending" || !order.paid}
            data-testid={`admin-order-resend-${order.id}`}
            className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {rs === "sending" ? (
              <>
                <RefreshCw size={12} className="animate-spin" /> Sending
              </>
            ) : rs === "ok" ? (
              <>
                <CheckCircle2 size={12} className="text-brand-red" /> Sent
              </>
            ) : rs === "err" ? (
              <>
                <XCircle size={12} className="text-red-500" /> Failed
              </>
            ) : (
              <>
                <Mail size={12} /> Resend Receipt
              </>
            )}
          </button>
        </div>
      </div>

      {expanded && order.shipping && (
        <div className="px-6 pb-5 -mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-neutral-400">
          <div>
            <p className="eyebrow mb-2">Ship to</p>
            <p className="text-white">{order.shipping.line1}</p>
            {order.shipping.line2 && <p>{order.shipping.line2}</p>}
            <p>
              {order.shipping.suburb} {order.shipping.state}{" "}
              {order.shipping.postcode}
            </p>
          </div>
          {order.notes && (
            <div>
              <p className="eyebrow mb-2">Notes</p>
              <p className="text-neutral-300 whitespace-pre-wrap">
                {order.notes}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Fulfilment stepper */}
      {order.paid && (
        <div
          className="border-t border-white/10 bg-white/[0.015] px-5 md:px-6 py-5"
          data-testid={`admin-order-fulfillment-${order.id}`}
        >
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <p className="eyebrow mr-4">Fulfilment</p>
            {FULFILLMENT_STEPS.map((s, i) => {
              const done = i <= activeIdx;
              const current = i === activeIdx;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(s.key)}
                  data-testid={`admin-order-step-${order.id}-${s.key}`}
                  className={`px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase border transition-colors ${
                    current
                      ? "bg-brand-red border-brand-red text-white"
                      : done
                      ? "border-white/30 text-white hover:bg-white/5"
                      : "border-white/10 text-neutral-600 hover:border-white/30 hover:text-white"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <input
              type="text"
              placeholder="Carrier (AusPost, Sendle...)"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              data-testid={`admin-order-carrier-${order.id}`}
              className="md:col-span-3 bg-[#0a0a0a] border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:border-white/40"
            />
            <input
              type="text"
              placeholder="Tracking number"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              data-testid={`admin-order-tracking-${order.id}`}
              className="md:col-span-4 bg-[#0a0a0a] border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:border-white/40"
            />
            <input
              type="url"
              placeholder="Tracking URL (optional)"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              data-testid={`admin-order-tracking-url-${order.id}`}
              className="md:col-span-3 bg-[#0a0a0a] border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:border-white/40"
            />
            <button
              onClick={saveTracking}
              data-testid={`admin-order-tracking-save-${order.id}`}
              className="md:col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/20 hover:bg-white hover:text-black text-white text-[10px] font-mono tracking-widest uppercase transition-colors"
            >
              <Save size={12} /> Save
            </button>
          </div>
          {order.tracking_number && (
            <p className="mt-3 text-[11px] text-neutral-500 font-mono">
              Current tracking:{" "}
              <span className="text-white">{order.tracking_number}</span>
              {order.tracking_url && (
                <>
                  {" · "}
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-red hover:underline"
                  >
                    view link
                  </a>
                </>
              )}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function EmailCustomerButton({ booking }) {
  const buildBody = () => {
    const first = (booking.name || "").split(" ")[0] || "there";
    const dateObj = booking.preferred_date
      ? new Date(booking.preferred_date + "T00:00:00")
      : null;
    const dateStr = dateObj
      ? dateObj.toLocaleDateString(undefined, {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      : booking.preferred_date;
    const svc = (booking.service || "detail")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return `Hi ${first},\n\nJust confirming your ${svc} booking with SEVEN7H for ${dateStr} at ${booking.preferred_time}.\n\nAddress: 39 Reynolds Drive, Paralowie SA 5108\n\nIf you need to reschedule or have any questions, please give me a call on 0429 052 189.\n\nSee you then!\nKosta\nSEVEN7H Car Detailing`;
  };
  const subject = "Your SEVEN7H booking is confirmed";
  const href = `mailto:${encodeURIComponent(booking.email || "")}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(buildBody())}`;

  return (
    <a
      href={href}
      data-testid={`admin-booking-email-${booking.id}`}
      title="Open your default email client with a pre-written confirmation to send from your own inbox"
      className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase transition-colors"
    >
      <Send size={12} /> Email
    </a>
  );
}

function BulkActionButton({ testId, state, onClick, label, confirmMessage }) {
  const handle = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(confirmMessage)) onClick();
  };
  return (
    <button
      onClick={handle}
      disabled={state === "sending"}
      data-testid={testId}
      className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-white/40 text-white text-[10px] font-mono tracking-widest uppercase disabled:opacity-40 transition-colors"
    >
      {state === "sending" ? (
        <>
          <RefreshCw size={12} className="animate-spin" /> Working
        </>
      ) : state === "ok" ? (
        <>
          <CheckCheck size={12} className="text-brand-red" /> Done
        </>
      ) : (
        <>
          <CheckCheck size={12} /> {label}
        </>
      )}
    </button>
  );
}

